/**
 * Секция видеоотзывов на /reviews/.
 *
 * До неё страница ОТСЫЛАЛА на YouTube вместо того, чтобы показать ролики:
 * шесть готовых отзывов клиентов жили на проде и не переезжали.
 *
 * Кадры грузятся лениво: шесть плееров в начальной загрузке стоили бы больше,
 * чем весь остальной вес страницы.
 */
import { videoTestimonials } from '@/data/videoTestimonials';
import AttorneyAdvertisingNote from '@/components/AttorneyAdvertisingNote';
import SchemaJsonLd from '@/components/SchemaJsonLd';
import { channelVideos } from '@/data/podcastEpisodes';

const SITE = 'https://www.morganlegalny.com';

/**
 * VideoObject на каждый ролик.
 *
 * 🔴 Название и длительность НЕ дублируются здесь, а берутся по идентификатору из
 * описи роликов канала: второй экземпляр перечня разъезжается с первым молча.
 * Если ролика в описи нет, разметка на него не выпускается вовсе — VideoObject
 * без `name` хуже отсутствующего: он объявляет машине пустое имя как имя.
 *
 * `uploadDate` и `duration` — из описи канала (первое снято у самого YouTube),
 * `thumbnailUrl` — кадр YouTube. Ни одна из величин не выведена из даты страницы.
 * 🔴 Ролик без даты публикации разметки не получает: у VideoObject `uploadDate`
 * обязателен, а подставить сюда «сегодня» значило бы солгать машине.
 */
function videoSchema() {
  return videoTestimonials
    .map((v) => {
      const meta = channelVideos.find((e) => e.id === v.id);
      if (!meta || !meta.uploadDate) return null;
      return {
        '@context': 'https://schema.org',
        '@type': 'VideoObject',
        name: meta.title,
        description: `Client video testimonial for Morgan Legal Group, P.C. — ${v.name}.`,
        thumbnailUrl: [`https://i.ytimg.com/vi/${v.id}/hqdefault.jpg`],
        uploadDate: meta.uploadDate,
        duration: meta.iso,
        embedUrl: `https://www.youtube-nocookie.com/embed/${v.id}`,
        contentUrl: `https://www.youtube.com/watch?v=${v.id}`,
        publisher: { '@id': `${SITE}/#organization` },
        isFamilyFriendly: true,
      };
    })
    .filter(Boolean);
}

export default function VideoTestimonials() {
  if (videoTestimonials.length === 0) return null;
  const schema = videoSchema();
  return (
    <section className="py-16 lg:py-20 bg-white border-t border-navy/5">
      {schema.length ? <SchemaJsonLd schema={schema} /> : null}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-8 lg:mb-10">
          <div className="section-label mb-4">In Their Own Words</div>
          <h2 className="font-display text-navy text-3xl sm:text-4xl font-bold leading-tight">
            Video <span className="text-gold italic">Reviews</span>
          </h2>
          <p className="font-body text-navy/70 text-base leading-relaxed mt-4">
            Clients who agreed to record a short testimonial. Videos are voluntary and clients are never compensated.
          </p>
        </div>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {videoTestimonials.map((v) => (
            <li key={v.id}>
              <figure className="m-0">
                <figcaption className="font-display text-navy text-lg font-bold mb-3 text-center">
                  {v.name}
                </figcaption>
                <div
                  className="relative w-full overflow-hidden border border-navy/10 bg-navy"
                  style={{ aspectRatio: '16 / 9' }}
                >
                  <iframe
                    src={`https://www.youtube-nocookie.com/embed/${v.id}`}
                    title={`Client video testimonial — ${v.name}`}
                    loading="lazy"
                    allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                    className="absolute inset-0 w-full h-full"
                  />
                </div>
                <AttorneyAdvertisingNote />
              </figure>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
